import { useEffect, useRef, useState } from 'react';
import { GAME_RULES } from '@continuum/show-core';
import type { DivideTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono, sans } from '../ui/atoms.js';
import { CrateSprite } from './art.js';

interface Item {
  id: number;
  /** Where the crew has cut so far, as fractions of the bar. */
  cuts: number[];
  done: boolean;
  /** Where the bar came to rest when it was done, as its left edge on the track. */
  restAt: number | null;
}

/**
 * QUARTERMASTER — tap to cut a fair share.
 *
 * Slice, but the point is fairness rather than speed: the cut has to land near the even split,
 * and the bar tells you honestly how close you were. Each bar slides from side to side on its
 * own track at its own pace (Marek, 2026-09-29), so the fair cut is a moving target: wait for
 * it, then tap. A tap on the empty track beside a bar is nothing, not a mistake. Rations,
 * toasts and the last pour are all the same gesture.
 */
export function Divide({ task, era, onProgress, onWin, onMistake, accent, onFrame, paused = false, s }: GameProps<DivideTask>) {
  const { tolerance, barWidth, swingMs } = GAME_RULES.divide;
  const [items, setItems] = useState<Item[]>(() =>
    Array.from({ length: task.items }, (_, id) => ({ id, cuts: [], done: false, restAt: null })),
  );
  /** Each bar starts somewhere different in its swing. */
  const [phases] = useState(() => Array.from({ length: task.items }, () => Math.random() * Math.PI * 2));
  const [wobble, setWobble] = useState<number | null>(null);
  const won = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };

  // The game's own clock, which stands still while something covers the board.
  const hold = useRef(paused);
  hold.current = paused;
  const stoppedAt = useRef<number | null>(null);
  const lost = useRef(0);
  const clock = () => (stoppedAt.current ?? performance.now()) - lost.current;
  useEffect(() => {
    if (paused && stoppedAt.current === null) stoppedAt.current = performance.now();
    if (!paused && stoppedAt.current !== null) {
      lost.current += performance.now() - stoppedAt.current;
      stoppedAt.current = null;
    }
  }, [paused]);
  const [, frame] = useState(0);
  useEffect(() => {
    let id = requestAnimationFrame(function step() {
      frame((n) => n + 1);
      id = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  /** A bar's left edge on its track, 0 to 1 − barWidth, easing in and out at each end. */
  const leftOf = (item: Item, at: number) => {
    if (item.restAt !== null) return item.restAt;
    const period = swingMs[item.id % swingMs.length] ?? 3_200;
    return (1 - barWidth) * (0.5 + 0.5 * Math.sin((2 * Math.PI * at) / period + (phases[item.id] ?? 0)));
  };

  /** The cuts a fair split would need: 2 shares → one cut at ½; 3 → ⅓ and ⅔. */
  const targets = Array.from({ length: task.shares - 1 }, (_, i) => (i + 1) / task.shares);

  const cut = (item: Item, onTrack: number) => {
    if (item.done || hold.current) return;
    const left = leftOf(item, clock());
    const at = (onTrack - left) / barWidth;
    // Beside the bar: nothing there to cut.
    if (at < 0 || at > 1) return;
    const nextIndex = item.cuts.length;
    const target = targets[nextIndex];
    if (target === undefined) return;

    if (Math.abs(at - target) > tolerance) {
      cbs.current.onMistake?.();
      setWobble(item.id);
      setTimeout(() => setWobble(null), 340);
      return;
    }

    setItems((prev) => {
      const next = prev.map((it) => {
        if (it.id !== item.id) return it;
        const done = it.cuts.length + 1 >= targets.length;
        return { ...it, cuts: [...it.cuts, at], done, restAt: done ? left : null };
      });
      const done = next.filter((it) => it.done).length;
      cbs.current.onProgress(Math.round((done / task.items) * 100));
      if (done >= task.items && !won.current) {
        won.current = true;
        // Not inside the state update: WELL DONE is another component's state.
        queueMicrotask(() => cbs.current.onWin());
      }
      return next;
    });
  };

  useEffect(() => {
    cbs.current.onFrame(
      items
        .filter((i) => i.cuts.length > 0)
        .map((i) => `d${i.id}`)
        .join(','),
    );
  }, [items]);

  const done = items.filter((i) => i.done).length;
  const now = clock();

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontFamily: mono,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 1.2,
          lineHeight: 1.4,
          color: accent,
          textAlign: 'center',
        }}
      >
        {s.divideHead(task.shares)}
      </div>

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item) => {
          const left = leftOf(item, now);
          return (
            <div
              key={item.id}
              data-nodrag
              role="button"
              onPointerDown={(e) => {
                const box = e.currentTarget.getBoundingClientRect();
                cut(item, (e.clientX - box.left) / box.width);
              }}
              style={{
                position: 'relative',
                height: 64,
                borderRadius: 12,
                border: '1px dashed rgba(255,255,255,.10)',
                background: 'rgba(255,255,255,.02)',
                cursor: 'pointer',
                touchAction: 'none',
              }}
            >
              {/* the bar itself, sliding along its track */}
              <div
                style={{
                  position: 'absolute',
                  top: 3,
                  bottom: 3,
                  left: `${left * 100}%`,
                  width: `${barWidth * 100}%`,
                  borderRadius: 10,
                  border: `1.5px solid ${wobble === item.id ? '#ff2e3f' : item.done ? accent : `${accent}88`}`,
                  background: item.done ? `${accent}26` : `${accent}12`,
                  boxShadow: item.done ? `0 0 18px ${accent}44` : 'none',
                  overflow: 'hidden',
                  animation: wobble === item.id ? 'ccGlitch .34s' : undefined,
                  pointerEvents: 'none',
                }}
              >
                {/* where a fair cut would land, riding on the bar */}
                {!item.done &&
                  targets.map((t, i) => (
                    <div
                      key={`g${i}`}
                      style={{
                        position: 'absolute',
                        left: `${(t - tolerance) * 100}%`,
                        width: `${tolerance * 200}%`,
                        top: 0,
                        bottom: 0,
                        background: `${accent}22`,
                        borderLeft: `1px solid ${accent}33`,
                        borderRight: `1px solid ${accent}33`,
                      }}
                    />
                  ))}
                {item.cuts.map((c, i) => (
                  <div
                    key={`c${i}`}
                    style={{
                      position: 'absolute',
                      left: `${c * 100}%`,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      marginLeft: -1,
                      background: accent,
                      boxShadow: `0 0 12px ${accent}`,
                    }}
                  />
                ))}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    fontFamily: sans,
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    color: item.done ? accent : `${accent}dd`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      display: 'block',
                      opacity: item.done ? 1 : 0.75,
                      animation: item.done ? 'ccPop .3s ease-out' : undefined,
                    }}
                  >
                    <CrateSprite era={era} />
                  </span>
                  {item.done ? s.divideEqual : s.divideCut(item.cuts.length + 1, targets.length)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}>
        <span style={{ fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: accent }}>{s.divideDone(done, task.items)}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: mono, fontSize: 12, color: '#8a9aa6' }}>{s.divideHint}</span>
      </div>
    </div>
  );
}
